import type { BusinessHours } from '@/types/settings';

export const DAY_ORDER: BusinessHours['day'][] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const DAY_SHORT: Record<BusinessHours['day'], string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

/** "09:00" → "9:00 AM". Hours are HH:mm wall-clock strings, no tz involved. */
export function formatTime12(value: string): string {
  const [hStr, mStr] = value.split(':');
  const hour24 = Number(hStr);
  const minute = mStr ?? '00';
  if (!Number.isFinite(hour24)) return value;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${h12}:${minute} ${period}`;
}

export function summarizeSchedule(ordered: BusinessHours[]): {
  daysLabel: string;
  hoursLabel: string;
} {
  const open = ordered.filter((h) => h.open);
  if (open.length === 0) return { daysLabel: 'Closed all week', hoursLabel: '' };

  const idx = open.map((h) => DAY_ORDER.indexOf(h.day)).sort((a, b) => a - b);
  const runs: Array<[number, number]> = [];
  let start = idx[0];
  let prev = idx[0];
  for (let i = 1; i < idx.length; i++) {
    if (idx[i] === prev + 1) {
      prev = idx[i];
    } else {
      runs.push([start, prev]);
      start = idx[i];
      prev = idx[i];
    }
  }
  runs.push([start, prev]);

  const daysLabel =
    idx.length === 7
      ? 'every day'
      : runs
          .map(([s, e]) =>
            s === e
              ? DAY_SHORT[DAY_ORDER[s]]
              : `${DAY_SHORT[DAY_ORDER[s]]}–${DAY_SHORT[DAY_ORDER[e]]}`,
          )
          .join(', ');

  const uniform = open.every(
    (h) => h.openTime === open[0].openTime && h.closeTime === open[0].closeTime,
  );
  const hoursLabel = uniform
    ? `${formatTime12(open[0].openTime)} – ${formatTime12(open[0].closeTime)}`
    : 'hours vary';

  return { daysLabel, hoursLabel };
}
